import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { usePaymentSettings } from "@/lib/payments/usePaymentSettings";

type ExpressMethod = "cod" | "manual_transfer";

export interface ExpressEligibility {
  loading: boolean;
  eligible: boolean;
  payMethod: ExpressMethod | null;
  addressCity: string | null;
  reason: string | null;
}

function normalizeCity(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

/**
 * Determines whether the current user can use the 1-click "express" checkout
 * from the cart drawer.
 *
 * Policy (decided with the user):
 *  - Card payments are NEVER eligible for 1-click (would require CVV re-entry).
 *  - Only `cod` or `manual_transfer` are eligible.
 *  - We need a default shipping address.
 *  - We pick the last-used eligible method from the user's order history;
 *    if none, we fall back to `manual_transfer` (always allowed).
 *  - If the preferred method is `cod` and the default address city is NOT in
 *    the COD allowlist, we fall back to `manual_transfer`.
 */
export function useExpressEligibility(userId: string | null | undefined): ExpressEligibility {
  const { settings } = usePaymentSettings();
  const [state, setState] = useState<ExpressEligibility>({
    loading: true,
    eligible: false,
    payMethod: null,
    addressCity: null,
    reason: null,
  });

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setState({ loading: false, eligible: false, payMethod: null, addressCity: null, reason: "no_user" });
      return;
    }
    (async () => {
      try {
        const [{ data: addr }, { data: lastOrder }] = await Promise.all([
          supabase
            .from("addresses")
            .select("city, is_default, created_at")
            .eq("user_id", userId)
            .eq("type", "shipping")
            .order("is_default", { ascending: false })
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from("orders")
            .select("payment_method, created_at")
            .eq("user_id", userId)
            .in("payment_method", ["cod", "manual_transfer"])
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);

        if (cancelled) return;

        if (!addr) {
          setState({ loading: false, eligible: false, payMethod: null, addressCity: null, reason: "no_address" });
          return;
        }

        const preferred: ExpressMethod =
          (lastOrder?.payment_method as ExpressMethod | undefined) || "manual_transfer";

        let resolved: ExpressMethod = preferred;

        // Validate COD vs city allowlist; fall back to transfer if not allowed.
        if (resolved === "cod") {
          const codEnabled = settings?.cod_enabled !== false;
          const allowed = settings?.cod_allowed_cities ?? [];
          const cityOk =
            !addr.city ||
            allowed.length === 0 ||
            allowed.some((c: string) => normalizeCity(c) === normalizeCity(addr.city ?? ""));
          if (!codEnabled || !cityOk) {
            resolved = "manual_transfer";
          }
        }

        // manual_transfer must be enabled in payment settings.
        if (resolved === "manual_transfer" && settings && settings.manual_transfer_enabled === false) {
          setState({
            loading: false,
            eligible: false,
            payMethod: null,
            addressCity: addr.city ?? null,
            reason: "no_payment_method",
          });
          return;
        }

        setState({
          loading: false,
          eligible: true,
          payMethod: resolved,
          addressCity: addr.city ?? null,
          reason: null,
        });
      } catch (e) {
        if (!cancelled) {
          setState({ loading: false, eligible: false, payMethod: null, addressCity: null, reason: "error" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, settings?.cod_enabled, settings?.manual_transfer_enabled, settings?.cod_allowed_cities]);

  return state;
}